import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent } from "@pdfking/components"
import type { ComponentChildren } from "preact"

import type { SidebarViewDefinition, SidebarViewProps } from "#public"
import type { PdfviewSidebarView } from "#public"

import { SIDEBAR_VIEWS } from "../sidebar/Views"

/**
 * The viewer's left rail (`children`, built by CreatePdfViewer) plus one panel
 * per {@link SIDEBAR_VIEWS} entry. Which panel is open — or none — is the
 * caller's `selected` state, so it lives in the URL like every other piece of
 * viewer state. In the page flow, not the shared sidebar's viewport-`fixed`
 * overlay: the viewers lay it out under their header (their `sidebar` slot), so
 * opening a panel narrows the document and leaves the header alone.
 */
export function ViewerSidebar({
  children,
  selected,
  ...view_props
}: SidebarViewProps & {
  children: ComponentChildren
  selected: PdfviewSidebarView | undefined
}) {
  const { debug } = view_props
  // Widened to the declared shape: `satisfies` keeps each entry's literal type,
  // on which an optional field the entry omits isn't visible at all.
  const candidate: SidebarViewDefinition | undefined = selected
    ? SIDEBAR_VIEWS[selected]
    : undefined
  // A route can still name the debug panel with `debug` off (a shared link, or
  // the flag flipping under a live tab) — the rail wouldn't offer it, so don't
  // render it either.
  const view = candidate && (debug || !candidate.debug_only) ? candidate : undefined
  return (
    <Sidebar className="w-auto" collapsible="none">
      <div className="flex h-full min-w-0 flex-row">
        {children}

        {view && (
          // `--sidebar-width` is the whole sidebar, rail included.
          <SidebarContent className="w-[calc(var(--sidebar-width)-var(--sidebar-width-icon))] border-r">
            {/* The open panel is just the view. It carries no title of its own:
                the rail button that opened it is lit and names itself on hover,
                so a heading would only repeat that — and it stole a line from
                the panel's own header (the file rows' column labels), which is
                the row that actually needs to be up there. */}
            <SidebarGroup className="flex-1">
              <SidebarGroupContent className="flex flex-1 flex-col">
                <view.component {...view_props} />
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        )}
      </div>
    </Sidebar>
  )
}
